import { useState } from "react";
import { useCoupons, useDeleteCoupon } from "@/hooks/use-orders";
import { useActiveStore } from "@/hooks/use-active-store";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { useDebounce } from "@/hooks/use-debounce";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CouponDialog } from "@/components/coupons/coupon-dialog";
import { Search, Plus, Pencil, Trash2 } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { TablePagination } from "@/components/ui/table-pagination";

const discountTypeLabels: Record<string, string> = {
  percent: "Postotak",
  fixed_cart: "Fiksni (košarica)",
  fixed_product: "Fiksni (proizvod)",
};

export function CouponsPage() {
  const { storeId } = useActiveStore();
  const { user } = useSession();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any>(null);
  const deleteMut = useDeleteCoupon(storeId);

  const isAdmin = hasRole(user, "admin") || hasRole(user, "manager");

  const filters: Record<string, string> = { page: String(page), per_page: "20" };
  if (debouncedSearch) filters.search = debouncedSearch;

  const { data, isLoading, error } = useCoupons(storeId, filters);

  function handleEdit(coupon: any) {
    setEditingCoupon(coupon);
    setDialogOpen(true);
  }

  function handleCreate() {
    setEditingCoupon(null);
    setDialogOpen(true);
  }

  async function handleDelete(coupon: any) {
    if (!confirm(`Obrisati kupon "${coupon.code}"?`)) return;
    try {
      await deleteMut.mutateAsync(coupon.id);
      toast.success("Kupon obrisan");
    } catch (err: any) {
      toast.error(err?.message || "Greška pri brisanju");
    }
  }

  if (!storeId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-xs">Odaberite trgovinu</p>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">Kuponi</h2>
        {isAdmin && (
          <Button size="sm" onClick={handleCreate}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Novi kupon
          </Button>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          className="h-8 text-xs pl-8"
          placeholder="Pretraži po kodu kupona..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {error && <p className="text-destructive text-xs">{(error as Error).message}</p>}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-2 text-left font-medium">Kod</th>
                <th className="p-2 text-left font-medium">Tip</th>
                <th className="p-2 text-right font-medium">Iznos</th>
                <th className="p-2 text-right font-medium hidden sm:table-cell">Korišteno</th>
                <th className="p-2 text-right font-medium hidden md:table-cell">Min. iznos</th>
                <th className="p-2 text-left font-medium hidden lg:table-cell">Ističe</th>
                {isAdmin && <th className="p-2 text-right font-medium w-20">Akcije</th>}
              </tr>
            </thead>
            <tbody>
              {data?.coupons?.map((c: any) => (
                <tr key={c.id} className="border-b hover:bg-muted/50 transition-colors">
                  <td className="p-2">
                    <Badge variant="secondary" className="font-mono text-[11px]">
                      {c.code}
                    </Badge>
                  </td>
                  <td className="p-2 text-muted-foreground">
                    {discountTypeLabels[c.discount_type] || c.discount_type}
                  </td>
                  <td className="p-2 text-right font-medium">
                    {c.discount_type === "percent" ? `${c.amount}%` : `${c.amount} KM`}
                  </td>
                  <td className="p-2 text-right hidden sm:table-cell">
                    {c.usage_count}{c.usage_limit ? ` / ${c.usage_limit}` : ""}
                  </td>
                  <td className="p-2 text-right text-muted-foreground hidden md:table-cell">
                    {c.minimum_amount ? `${c.minimum_amount} KM` : "—"}
                  </td>
                  <td className="p-2 text-muted-foreground hidden lg:table-cell">
                    {c.expiry_date ? formatDate(c.expiry_date) : "—"}
                  </td>
                  {isAdmin && (
                    <td className="p-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => handleEdit(c)}
                          title="Uredi"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => handleDelete(c)}
                          title="Obriši"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {data?.coupons?.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="p-6 text-center text-muted-foreground">
                    Nema kupona
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && <TablePagination page={page} totalPages={data.totalPages} onPageChange={setPage} />}

      <CouponDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        coupon={editingCoupon}
      />
    </div>
  );
}
